// API
import LogisticsApi from '@/api/LogisticsApi';

// Utils
import AudioUtil, { soundNames } from '@/utils/AudioUtil';
import ErrorUtil from '@/utils/ErrorUtil';
import ScanUtil from '@/utils/ScanUtil';

// Mutations
export const mutationTypes = {
    CLEAR_PENDING: 'CLEAR_PENDING',
    PUSH_SCANNED_ITEM: 'PUSH_SCANNED_ITEM',
    PUSH_SHELVED_ITEM: 'PUSH_SHELVED_ITEM',
    REMOVE_SCANNED_ITEM: 'REMOVE_SCANNED_ITEM',
    RESET_LOCATOR: 'RESET_LOCATOR',
    RESET_SCANNED_ITEMS: 'RESET_SCANNED_ITEMS',
    SET_SCAN_STRING: 'SET_SCAN_STRING',
    SET_LOCATOR: 'SET_LOCATOR',
    SET_SCANNED_ITEM: 'SET_SCANNED_ITEM',
    SET_SHELF_IN_FLIGHT: 'SET_SHELF_IN_FLIGHT',
    SET_SHELVED_ITEM: 'SET_SHELVED_ITEM',
    RESET_SHELVED_ITEMS: 'RESET_SHELVED_ITEMS',
};

export const actionTypes = {
    processScan: 'processScan',
    scanSerial: 'scanSerial',
    scanShelf: 'scanShelf',
    shelve: 'shelve',
    reset: 'reset',
};

export default {
    namespaced: true,
    state: {
        /**
         * @type { String }
         */
        scanString: '',
        /**
         * @type { Object }
         */
        locator: {},
        /**
         * @type { Object[] }
         */
        scannedItems: [],
        /**
         * @type { Object[] }
         */
        shelvedItems: [],
        /**
         * @type { Boolean }
         */
        shelfInFlight: false,
    },
    getters: {
        /**
         * @param { Object[] } scannedItems - The scanned items
         * @returns { Boolean } - true for any failed item, false for all success
         */
        hasFailedItems: ( { scannedItems } ) => scannedItems.some( ( item ) => item.status === 'error' ),
    },
    actions: {
        [ actionTypes.reset ]( { commit } ) {
            commit( mutationTypes.SET_SCAN_STRING, { scanString: '' } );
            commit( mutationTypes.SET_LOCATOR, { locator: {} } );
            commit( mutationTypes.RESET_SCANNED_ITEMS );
            commit( mutationTypes.RESET_SHELVED_ITEMS );
            commit( mutationTypes.SET_SHELF_IN_FLIGHT, { shelfInFlight: false } );
        },

        async [ actionTypes.processScan ]( {
            dispatch,
            commit,
            state,
        } ) {
            const scan = state.scanString;
            if ( ScanUtil.isEmptyString( scan ) ) { return; }
            if ( ScanUtil.isSerialRef( scan, 'mi' ) ) {
                commit( mutationTypes.SET_SCAN_STRING, { scanString: '' } );
                await dispatch( actionTypes.scanSerial, { serial: scan } );
            } else if ( !ScanUtil.isNumeric( scan ) ) {
                commit( mutationTypes.SET_SCAN_STRING, { scanString: '' } );
                dispatch( 'snackbar', {
                    message: 'Not a valid ID',
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
            } else {
                commit( mutationTypes.SET_SCAN_STRING, { scanString: '' } );
                await dispatch( actionTypes.scanShelf, { serial: scan } );
            }
        },
        async [ actionTypes.scanSerial ]( {
            state,
            // getters,
            commit,
            dispatch,
            rootState,
            rootGetters,
        }, { serial, noBeeps } ) {
            if ( ScanUtil.hasItem( serial, state.scannedItems ) ) {
                commit( mutationTypes.SET_SCANNED_ITEM, {
                    serial, status: 'loading',
                } );
            } else {
                commit( mutationTypes.PUSH_SCANNED_ITEM, {
                    serial, title: '', source: '', status: 'loading',
                } );
            }
            try {
                const { data } = await LogisticsApi.getMailItemRef( {
                    jwtToken: rootState.User.jwtToken,
                    websiteId: rootGetters[ 'Website/id' ],
                    userAccountId: rootState.User.accountId,
                    mailItemRef: serial,
                } );
                commit( mutationTypes.SET_SCANNED_ITEM, {
                    serial,
                    id: data.mailItem.id,
                    customer: data.mailItem.customerFullName,
                    // This is the most consistent way to get just the name
                    // We should request a change to this DTO to return the name
                    source: data.mailItem.locatorDescription ? data.mailItem?.locatorDescription.split( ' ' ).pop() : '',
                    status: 'pending',
                } );
                if ( state.locator.id ) {
                    await dispatch( actionTypes.shelve, { mailItemId: data.mailItem.id, serial } );
                }
                if ( !noBeeps ) {
                    await AudioUtil.playSound( soundNames.SUCCESS_SHORT );
                }
            } catch ( e ) {
                dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( e ),
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                commit( mutationTypes.SET_SCANNED_ITEM, {
                    serial,
                    status: 'error',
                    message: ErrorUtil.getMessageFromError( e ),
                } );
                if ( !noBeeps ) {
                    await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                }
            }
        },
        async [ actionTypes.scanShelf ]( {
            state,
            getters,
            commit,
            dispatch,
            rootState,
            rootGetters,
        }, { serial: locatorRef } ) {
            if ( state.locator?.id ) {
                // New Shelf -- Reset Scanned Items
                commit( mutationTypes.RESET_SCANNED_ITEMS );
            }
            try {
                commit( mutationTypes.SET_SHELF_IN_FLIGHT, { shelfInFlight: true } );
                const { data } = await LogisticsApi.getLocator( {
                    jwtToken: rootState.User.jwtToken,
                    websiteId: rootGetters[ 'Website/id' ],
                    userAccountId: rootState.User.accountId,
                    locatorRef,
                } );
                if ( !data?.locationLocator?.id ) {
                    // Alert user that no locator
                    throw new Error( 'Locator not found' );
                } else if ( data.locationLocator && ( data.locationLocator.type === 'Delivery' ) ) {
                    throw new Error( 'Cannot Shelve to a Delivery locator' );
                } else {
                    await AudioUtil.playSound( soundNames.SUCCESS_LONG );
                }
                commit( mutationTypes.SET_LOCATOR, {
                    locator: data.locationLocator,
                } );
                if ( state.scannedItems?.length > 0 ) {
                    await Promise.all( state.scannedItems.map( async ( { id, serial } ) => {
                        try {
                            await dispatch( actionTypes.shelve, { mailItemId: id, serial } );
                        } catch ( e ) {
                            commit( mutationTypes.SET_SCANNED_ITEM, {
                                serial,
                                status: 'error',
                                message: 'Error processing mail item',
                            } );
                            dispatch( 'snackbar', {
                                message: 'Error processing mail item',
                                type: 'error',
                                duration: 5000,
                            }, { root: true } );
                        }
                    } ) );
                    if ( getters.hasFailedItems ) {
                        await AudioUtil.playSound( soundNames.FAILURE_LONG );
                    } else {
                        await AudioUtil.playSound( soundNames.SUCCESS_LONG );
                    }
                }
            } catch ( e ) {
                dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( e ),
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                await AudioUtil.playSound( soundNames.FAILURE_LONG );
                commit( mutationTypes.SET_LOCATOR, { locator: {} } );
            } finally {
                commit( mutationTypes.SET_SHELF_IN_FLIGHT, { shelfInFlight: false } );
            }
        },
        async [ actionTypes.shelve ]( {
            state,
            commit,
            rootState,
            rootGetters,
        }, { mailItemId, serial } ) {
            // Thrown Errors are to be handled by caller
            const { data } = await LogisticsApi.addMailToShelve( {
                jwtToken: rootState.User.jwtToken,
                websiteId: rootGetters[ 'Website/id' ],
                userAccountId: rootState.User.accountId,
                mailItemId,
                moveToLocatorId: state.locator.id,
            } );

            // Update Scanned Item table and item to history
            commit( mutationTypes.SET_SCANNED_ITEM, {
                serial,
                source: data.sourceLocationLocator?.name || data.sourceLocationLocator?.description || data.sourceLocationLocator?.type,
                destination: data.destLocationLocator.name,
                status: 'complete',
            } );
            const shelvedItem = state.scannedItems.find( ( item ) => item.serial === serial );
            // But Tyler, What is subId and why would you use the array length? Normally the serial would be the unique key
            // for the table, but since this is history the same serial can be used several times. Unlike the other tables,
            // items cannot be removed from this table. So we can safely use the array length (starting at 0) as the key
            commit( mutationTypes.PUSH_SHELVED_ITEM, { ...shelvedItem, subId: state.shelvedItems.length } );

            // Update Locator Count
            commit( mutationTypes.SET_LOCATOR, {
                locator: {
                    ...state.locator,
                    mailItemCount: data.destLocationLocator.mailItemCount,
                },
            } );
        },
    },
    mutations: {
        [ mutationTypes.SET_SCAN_STRING ]( state, { scanString } ) {
            state.scanString = scanString;
        },
        [ mutationTypes.SET_LOCATOR ]( state, { locator } ) {
            state.locator = locator;
        },
        [ mutationTypes.SET_SHELF_IN_FLIGHT ]( state, { shelfInFlight } ) {
            state.shelfInFlight = shelfInFlight;
        },
        [ mutationTypes.PUSH_SCANNED_ITEM ]( state, scannedItem ) {
            state.scannedItems = [ scannedItem, ...state.scannedItems ];
        },
        [ mutationTypes.PUSH_SHELVED_ITEM ]( state, shelvedItem ) {
            state.shelvedItems = [ shelvedItem, ...state.shelvedItems ];
        },
        [ mutationTypes.SET_SCANNED_ITEM ]( state, {
            serial, id, customer, source, destination, status, message,
        } ) {
            const itemToChange = state.scannedItems.find( ( item ) => item.serial.toString().toLowerCase() === serial.toString().toLowerCase() );
            itemToChange.serial = serial.toString().toLowerCase().startsWith( 'mi' )
                ? serial
                : `MI${ serial }`;
            itemToChange.id = id || itemToChange.id || '';
            itemToChange.customer = customer || itemToChange.customer || '';
            itemToChange.source = source || itemToChange.source || '';
            itemToChange.status = status || itemToChange.status || '';
            itemToChange.destination = destination || itemToChange.destination || '';
            itemToChange.message = message || '';
        },
        [ mutationTypes.RESET_SCANNED_ITEMS ]( state ) {
            state.scannedItems = [];
        },
        [ mutationTypes.RESET_SHELVED_ITEMS ]( state ) {
            state.shelvedItems = [];
        },
        [ mutationTypes.RESET_LOCATOR ]( state ) {
            if ( state.scannedItems?.length > 0 ) {
                state.scannedItems = state.scannedItems.map( ( item ) => ( {
                    ...item,
                    source: state.locator?.name,
                    destination: '',
                    status: 'pending',
                } ) );
            }
            state.locator = {};
        },
        [ mutationTypes.REMOVE_SCANNED_ITEM ]( state, { serial } ) {
            state.scannedItems = state.scannedItems.filter( ( item ) => item.serial !== serial );
        },
    },
};
